import {
  Button,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import {
  getOrganizationEmailTemplates,
  getOrganizationId,
  resetTemplateApi,
  updateEmailTemplate,
} from 'src/services/user.service';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import FormProvider, {
  RHFEditor,
  RHFTextField,
} from 'src/components/hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { Editor } from '@tinymce/tinymce-react';
import { useBoolean } from 'src/hooks/use-boolean';
import RHFTinyEditor from 'src/components/hook-form/rhf-tiny-editor';
import { useErrorBoundary } from 'react-error-boundary';

const useStyles = makeStyles(theme => ({
  messageDiv: {
    gap: 20,
  },
  accordianDiv: {
    padding: '24px 20px',
  },
  description: {
    color: theme.palette.content.primaryBlack3,
  },
}));

const TEMPLATE_TO_UI_MAPPING = {
  APPLICATION: {
    text: 'Application Submission Email',
    description:
      'Candidate receives a confirmation email after submitting their application successfully.',
  },
  REJECTED: {
    text: 'Application Rejection Notification',
    description:
      'Candidates will be notified via email in the event of their job application being rejected.',
  },
  SHORTLISTED: {
    text: 'Application Shortlisted Notification Email',
    description: 'Candidates shortlisted receive an email notification.',
  },
  INPERSON_INTERVIEW: {
    text: 'Interview Scheduled Notification',
    description:
      'Candidates receive an email notification for their interview.',
  },
  REMINDER: {
    text: 'AI Interview Reminder Email',
    description:
      'Candidates will receive an email reminder if the AI interview remains pending within the next 24 hours or if it is manually resent.',
  },
  REJECTION_INTERVIEW: {
    text: 'Rejection - interview email',
    description: 'Description',
  },
  REGENERATE_INTERVIEW: {
    text: 'Regenerate interview link email',
    description: 'Description',
  },
  HIRED: {
    text: 'Hired email',
    description: 'Description',
  },
  REJECTION_INPERSON: {
    text: 'Rejection - in person interview email',
    description: 'Description',
  },
};

export default function EmailTemplates({ showTemplatesList }) {
  // hooks
  const classes = useStyles();
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);
  const { showBoundary } = useErrorBoundary();

  // states
  const [emailTemplates, setEmailTemplates] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const isEditModalOpen = useBoolean(false);
  const showConfirmationMessage = useBoolean(false);
  const isSubmitting = useBoolean(false);

  // functions
  const getTemplate = () => {
    getOrganizationEmailTemplates(organization_id)
      .then(res => {
        if (res.success) {
          const templates = res.body.email_templates;
          setEmailTemplates({
            APPLICATION: templates?.APPLICATION,
            SHORTLISTED: templates?.SHORTLISTED,
            REJECTED: templates?.REJECTED,
            REMINDER: templates?.REMINDER,
            INPERSON_INTERVIEW: templates?.INPERSON_INTERVIEW,
          });
        } else throw new Error(res.message);
      })
      .catch(err => {
        console.log(err);
        showBoundary(err);
      });
  };

  const defaultValues = useMemo(
    () => ({
      subject: '',
      description: '',
    }),
    [],
  );

  const Schema = Yup.object().shape({
    subject: Yup.string().required('Subject is required'),
    description: Yup.string().required('Email Description is required'),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const { handleSubmit } = methods;

  const handleOnEdit = item => {
    methods.setValue('subject', emailTemplates?.[item]?.email_subject);
    methods.setValue('description', emailTemplates?.[item]?.email_body);
    setSelectedTemplate(item);
    isEditModalOpen.onTrue();
  };

  const handleOnModalClose = () => {
    isEditModalOpen.onFalse();
    isSubmitting.onFalse();
    showConfirmationMessage.onFalse();
    methods.setValue('subject', '');
    methods.setValue('description', '');
    setSelectedTemplate(null);
  };

  const handleOnReset = async () => {
    try {
      const payload = {
        template_type: selectedTemplate,
      };

      const response = await resetTemplateApi(payload, organization_id);
      if (response.success) {
        getTemplate();
        const subject = response?.body?.email_template?.email_subject;
        const description = response?.body?.email_template?.email_body;
        methods.setValue('subject', subject || '');
        methods.setValue('description', description || '');
      } else throw new Error(response.message);
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = handleSubmit(async (data, event) => {
    isSubmitting.onTrue();
    const payload = {
      template_type: selectedTemplate,
      email_template: {
        email_subject: data?.subject,
        email_body: data?.description,
      },
    };
    updateEmailTemplate(payload, organization_id)
      .then(res => {
        enqueueSnackbar('Update Successfull');
        getTemplate();
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        isSubmitting.onFalse();
        handleOnModalClose();
      });
  });

  // effects
  useEffect(() => {
    getTemplate();
  }, []);

  return (
    emailTemplates && (
      <>
        <Stack className={classes.messageDiv}>
          {Object.keys(emailTemplates)?.map(
            (item, index) =>
              (!showTemplatesList || showTemplatesList.includes(item)) && (
                <MuiAccordion
                  key={index}
                  slotProps={{ transition: { unmountOnExit: true } }}
                  elevation={2}
                  square
                  expanded={expanded === item}
                  onChange={() => {
                    setExpanded(prev => (prev === item ? null : item));
                  }}
                  className={classes.accordianDiv}
                >
                  <MuiAccordionSummary>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Stack spacing="12px">
                        <Typography variant="subheading2">
                          {TEMPLATE_TO_UI_MAPPING[item]?.text}
                        </Typography>
                        <Typography
                          variant="input"
                          className={classes.description}
                        >
                          {TEMPLATE_TO_UI_MAPPING[item]?.description}
                        </Typography>
                      </Stack>
                      {item === expanded && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOnEdit(item)}
                          startIcon={<Iconify icon="tabler:pencil" />}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>
                  </MuiAccordionSummary>
                  <MuiAccordionDetails>
                    <Stack spacing="16px">
                      <Stack spacing="12px">
                        <Typography variant="paragraph" fontWeight={600}>
                          Subject
                        </Typography>
                        <Typography variant="input">
                          {emailTemplates[item]?.email_subject}
                        </Typography>
                      </Stack>
                      <Stack spacing="12px">
                        <Typography variant="paragraph" fontWeight={600}>
                          Body
                        </Typography>
                        <Typography
                          dangerouslySetInnerHTML={{
                            __html: emailTemplates[item]?.email_body,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </MuiAccordionDetails>
                </MuiAccordion>
              ),
          )}
        </Stack>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Dialog
            open={isEditModalOpen.value}
            onClose={handleOnModalClose}
            maxWidth
          >
            <DialogContent>
              {showConfirmationMessage.value ? (
                <Stack spacing="17px" width="360px">
                  <Iconify icon="tabler:edit" width={48} />
                  <Stack spacing="8px">
                    <Typography variant="h4">
                      Are you sure you want to change the template?
                    </Typography>
                    <Typography variant="input" color="content.primaryBlack3">
                      It&apos;ll take upto 30 mins to update
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing="16px" width="760px">
                  <Stack spacing="6px">
                    <Typography variant="subtitle1">Subject</Typography>
                    <RHFTextField name="subject" />
                  </Stack>
                  <Stack spacing="16px">
                    <Typography variant="subtitle1">Body</Typography>
                    <RHFTinyEditor name="description" />
                  </Stack>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              {showConfirmationMessage.value ? (
                <Stack direction="row" alignItems="center" spacing="12px">
                  <Button
                    variant="soft"
                    onClick={() => {
                      showConfirmationMessage.onFalse();
                    }}
                  >
                    No
                  </Button>
                  <LoadingButton
                    loading={isSubmitting.value}
                    variant="contained"
                    onClick={onSubmit}
                  >
                    Yes
                  </LoadingButton>
                </Stack>
              ) : (
                <Stack
                  direction="row"
                  width="100%"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing="12px">
                    <Button onClick={handleOnModalClose} variant="soft">
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        showConfirmationMessage.onTrue();
                      }}
                    >
                      Save
                    </Button>
                  </Stack>
                  <Button onClick={handleOnReset}>Reset</Button>
                </Stack>
              )}
            </DialogActions>
          </Dialog>
        </FormProvider>
      </>
    )
  );
}

EmailTemplates.propTypes = {
  showTemplatesList: PropTypes.arrayOf(PropTypes.string),
};
